<template>
  <div>
    <div class="row">
      <BaseErrors :errors="errors"></BaseErrors>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
          <BaseLoader :loading="loading"/>

          <div v-if="recipe">
            <h1 class="card-title" :class="{'with-padding': loggedIn}">
              {{ recipe.name }}
              <small  class="actions">
                <a :href="'/app/'" class="btn btn-secondary btn-sm">Back</a>
                <a v-if="loggedIn" :href="'/app/recipes/' + recipeId + '/edit'" class="btn btn-secondary btn-sm">Edit</a>
              </small>
            </h1>

            <h5 class="card-subtitle mb-2 text-muted">{{recipe.subStyle}}</h5>

            <p class="card-text">{{recipe.comment}}</p>

            <div class="two-columns">
              <div class="main-widgets">
                <div class="three-columns">
                  <div class="recipe-values-component">
                    <h5 class="sub-head">
                      <BaseIcon name="poll-icon"
                                viewBox="0 0 24 24"
                                cssClass="ingredient-icon"
                                width="25"
                                height="25"/>
                      Recipe values
                    </h5>
                    <div class="beer-graph">
                      <MinMaxGraph name="og"  :min="1.015" :max="1.100" :selectedVal="recipe.og  | toFloat">{{recipe.og | sg}}</MinMaxGraph>
                      <MinMaxGraph name="fg"  :min="1.00"  :max="1.025" :selectedVal="recipe.fg  | toFloat">{{recipe.fg | sg}}</MinMaxGraph>
                      <MinMaxGraph name="ibu" :min="10"    :max="80"    :selectedVal="recipe.ibu | toFloat">{{recipe.ibu}}</MinMaxGraph>
                      <MinMaxGraph name="abv" :min="2"     :max="12"    :selectedVal="recipe.abv | toFloat">{{recipe.abv | abv}}</MinMaxGraph>
                      <MinMaxGraph name="ebc" :min="1"     :max="100"   :selectedVal="recipe.color | toFloat">{{recipe.color}}</MinMaxGraph>
                    </div>
                  </div>

                  <div class="malt-proportions-component">
                    <h5 class="sub-head">
                      <BaseIcon name="donut-icon"
                                viewBox="0 0 24 24"
                                cssClass="ingredient-icon"
                                width="25"
                                height="25"/>
                      Malt proportions
                    </h5>
                    <pie-chart
                      v-if="recipe.ingredientSchedules && recipe.ingredientSchedules.length > 1"
                      :ingredientSchedules="recipe.ingredientSchedules"
                    ></pie-chart>
                  </div>
                </div>

                <div class="three-columns">
                  <div class="fermentables-component">
                    <h5 class="sub-head">
                      <BaseIcon name="grain-icon"
                                viewBox="0 0 100 100"
                                cssClass="ingredient-icon"
                                width="25"
                                height="25"/>
                      Fermentables
                    </h5>
                    <ul class="ingredients-list">
                      <li v-if="!recipe.ingredientSchedules.length">Nothing added</li>
                      <li v-for="item in recipe.ingredientSchedules">

                        <RecipeShowListItem
                          :item="item"
                          :name="item.name"
                          :sub-text="[item.country, item.producer, item.variant] | joinedList"
                          :amount="item.amount | grainWeight"
                          type="ingredient"
                          :badges="[
                   `${item.percentage}%`,
                   `GP ${item.gravityPoints}`,
                   `EBC ${item.color}`
                  ]"
                        />

                      </li>
                      <li v-if="recipe.ingredientSchedules.length">
                        <RecipeShowListItem
                          :item="{}"
                          name="Total grain weight"
                          sub-text=""
                          :amount="recipe.totalWeight | grainWeight"
                          type="ingredient"
                          :badges="[]"
                        />
                      </li>
                    </ul>
                  </div>

                  <div class="hops-component">
                    <h5 v-if="recipe.hopSchedules.length" class="sub-head">
                      <BaseIcon name="hop-icon"
                                viewBox="0 0 60 70"
                                cssClass="ingredient-icon"
                                width="25"
                                height="25"/>
                      Hops
                    </h5>
                    <ul v-if="recipe.hopSchedules.length" class="ingredients-list">
                      <li v-for="item in recipe.hopSchedules">
                        <RecipeShowListItem
                          :item="item"
                          :name="`${item.name} | ${item.use} ${item.hopUseId === 3 ? item.time[2].val : item.time[0].val } ${item.hopUseId === 3 ? item.time[2].unit : item.time[0].unit }`"
                          :sub-text="[item.origin, item.producer] | joinedList"
                          :amount="item.amount | hopsWeight"
                          type="hop"
                          :badges="[
                    `${item.form}`,
                    `AA ${item.aa}%`,
                    `IBU ${item.ibu}`
                  ]"
                        />
                      </li>
                    </ul>
                  </div>

                  <div>
                    <div class="yeasts-component">
                      <h5 v-if="recipe.yeastSchedules.length" class="sub-head">
                        <BaseIcon name="yeast-icon"
                                  viewBox="0 0 100 100"
                                  cssClass="ingredient-icon"
                                  width="25"
                                  height="25"/>
                        Yeast
                      </h5>
                      <ul v-if="recipe.yeastSchedules.length" class="ingredients-list">
                        <li v-for="item in recipe.yeastSchedules">
                          <RecipeShowListItem
                            :item="item"
                            :name="`${item.code} / ${item.name}`"
                            :sub-text="`${item.laboratory}`"
                            :amount="'1 pk'"
                            type="yeast"
                            :badges="[
                    `${item.attenuation}%`,
                  ]"

                          />
                        </li>
                      </ul>
                    </div>
                    <div class="other-ingredient-component">
                      <h5 v-if="recipe.otherIngredientSchedules.length" class="sub-head">
                        <BaseIcon name="add-circle"
                                  viewBox="0 0 24 24"
                                  cssClass="ingredient-icon"
                                  width="25"
                                  height="25"/>
                        Other
                      </h5>
                      <ul v-if="recipe.otherIngredientSchedules.length" class="ingredients-list">
                        <li v-for="item in recipe.otherIngredientSchedules">
                          <RecipeShowListItem
                            :item="item"
                            :name="`${item.name} | ${item.use} ${item.time[0].val} ${item.time[0].unit}`"
                            :sub-text="[item.origin, item.producer] | joinedList"
                            :amount="item.amount | hopsWeight"
                            type="other-ingredient"
                            :badges="[
                    `${item.type}`,
                  ]"
                          />
                        </li>
                      </ul>
                    </div>
                    <div class="mash-schedules-component">
                      <h5 v-if="recipe.mashSchedules.length" class="sub-head">Mash schedules</h5>
                      <div v-if="recipe.mashSchedules.length" class="row">
                        <div class="col-sm-6">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="brew-sessions-component">
                <h5 class="sub-head">
                  <BaseIcon name="chart-icon"
                            viewBox="0 0 24 24"
                            cssClass="ingredient-icon"
                            width="25"
                            height="25"/>
                  Brew sessions
                </h5>
                <BaseLoader :loading="loadingBrewSessions" />

                <div class="brew-sessions-wrapper">
                  <ul class="brew-sessions">
                    <li v-if="brewSessions && !brewSessions.length">No brew session data available</li>
                    <li class="brew-session" v-for="brewSession in brewSessions">
                      <h6 class="brew-date">
                        {{brewSession.date}}
                        <div class="actions">
                          <router-link class="btn btn-outline-secondary btn-sm"
                                       v-if="loggedIn"
                                       :to="{ name: 'brew-session-edit', params: { id: brewSession.id }}">Edit</router-link>
                          <span v-if="brewSession.live" class="badge badge-danger">live</span>
                        </div>

                      </h6>

                      <h6>Fermentation graph</h6>
                      <div class="graph">
                        <FermGraph :brewSessionId="brewSession.id"></FermGraph>
                        <small class="last-updated" v-if="brewSession.live">
                          <time :datetime="brewSession.lastUpdate.timestamp">
                            last updated: {{brewSession.lastUpdate.timestamp | timeAgo}}
                          </time>
                        </small>
                      </div>

                      <h6>Measured values</h6>
                      <ul class="brew-actuals">
                        <li class="brew-og">
                          <span>OG</span>
                          <span>{{brewSession.og | sg | none}}</span>
                        </li>
                        <li class="brew-fg">
                          <span>FG</span>
                          <span>{{brewSession.fg | sg | none}}</span>
                        </li>
                        <li class="brew-abv">
                          <span>ABV</span>
                          <span>{{brewSession.abv | abv | none}}</span>
                        </li>
                        <li class="brew-volume">
                          <span>Vol</span>
                          <span>{{brewSession.volume | volume | none}} {{brewSession.volume | volumeUnit | none}}</span>
                        </li>
                      </ul>


                      <div v-if="brewSession.notes.length" class="brew-session-notes">
                        <h6>Notes</h6>
                        <ul class="notes-list">
                          <li v-for="note in brewSession.notes">
                            <span class="note-date">{{note.date}}</span>
                            <span class="note-content" v-html="note.content"></span>
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ul>

                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    import { HTTP } from '../lib/http';
    import { EventBus } from '../lib/event-bus.js';
    import PieChart from "../components/charts/PieChart";
    import FermGraph from "../components/FermGraph";
    import MinMaxGraph from "../components/MinMaxGraph.vue";
    import RecipeShowListItem from "../components/bits/RecipeShowListItem";


    export default {
        name: "recipeView",
        components: {
            PieChart,
            FermGraph,
            MinMaxGraph,
            RecipeShowListItem
        },
        data() {
            return {
                recipe: undefined,
                errors: null,
                loading: true,
                recipeId: null,
                brewSessions: null,
                loadingBrewSessions: true,
                loggedIn: false
            };
        },
        computed: {
            volume() {
                var vol = this.recipe.volume[0];
                return vol.val + " " + vol.unit;
            }
        },
        methods: {
            fetchRecipe() {
                this.loading = true;
                HTTP
                    .get("recipes/" + this.recipeId)
                    .then(response => {
                        this.recipe = response.data.data;
                    })
                    .catch(err => {
                        this.errors = [err.response.data.message];
                    })
                    .then(() => {
                        this.loading = false;
                    });
            },
            fetchBrewSessions() {
                this.loadingBrewSessions = true;
                HTTP
                    .get("recipes/" + this.recipeId + "/brew-sessions/")
                    .then(response => {
                        this.brewSessions = response.data.data;
                    })
                    .catch(err => {
                        this.errors = [err.response.data.message];
                    })
                    .then(() => {
                        this.loadingBrewSessions = false;
                    });
            },
        },
        beforeMount() {
            this.loggedIn = !!localStorage.username;
            EventBus.$on('logged-in', val => { this.loggedIn = val; });
        },
        mounted() {
            this.recipeId = this.$route.params.id;
            this.fetchRecipe();
            this.fetchBrewSessions();
        }
    };
</script>

<style scoped lang="scss">
  @import "/../assets/scss/_variablesx.scss";

  $gray-base: lighten($gray-900, 15);

  .card-title.with-padding { padding-right: 1.3em; }

  small.actions {
    position: absolute;
    top: 15px;
    right: 20px;
    max-width: 100px;
    text-align: right;

    a {
      font-size: .4em;
      padding: .5em 1em;
    }
  }

  .card-text { margin-top: 1em; }

  .sub-head {
    display: flex;
    align-items: center;
    margin-top: 1.5em;
    margin-bottom: 0;
    font-weight: 700;

    & > div { margin-right: .2em; }
    & + div { margin-top: 1em; }
  }

  .ingredients-list {
    padding: 0;
    list-style-type: none;

    li {
      position: relative;
      border-top: 1px solid $gray-base;

      &:first-child { border-top: none; }

      ::v-deep svg { fill: $gray-base; }
    }
  }

  .brew-sessions-wrapper {
    .brew-sessions {
      list-style-type: none;
      padding: 0;
      margin: 0;

      .brew-session {
        margin-bottom: 2.5em;
        margin-top: 1em;

        .brew-date {
          font-size: 1em;
          font-weight: 700;
          position: relative;

          .actions {
            position: absolute;
            right: 0;
            top: 0;

            .badge {
              margin-left: .5em;
            }
            .btn {
              font-size: .6em;
            }
          }
        }

        .last-updated {
          display: block;
          text-align: center;
        }
      }
      h6 {
        margin-top: 1em;
      }

      .graph {
        margin-left: -20px;
        margin-right: -20px;
      }
    }

    .notes-list {
      list-style-type: none;
      padding: 0;

      li {
        margin-bottom: 1em;
        position: relative;
        display: flex;

        .note-date {
          margin-right: 1em;
        }

        .note-content {
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .note-date {
          display: block;
        }
      }

    }

    .brew-actuals {
      list-style-type: none;
      padding: 0;
      margin: 0;
      display: flex;

      li {
        margin: 0 1em;
        text-align: center;
      }

      li > span {
        display: block;
        min-width: 50px;
        font-size: .8em;

        &:first-child {
          border-bottom: 2px solid;
        }
      }
    }
  }
  @media (min-width: 575.98px) {
    .ingredients-list {
      margin-right: 1em;
    }

    .card-title {
      font-size: 5em;
      font-weight: 700;
    }
    .card-subtitle {
      font-size: 2.5em;
    }
    .card-text {
      max-width: 800px;
      font-size: 1.5em;
    }
    .sub-head {
      font-size: 1.7em;
    }
    .two-columns {
      display: flex;
      flex-wrap: wrap;

      .main-widgets { flex-grow: 3; }
      .brew-sessions-component { flex-grow: 1; max-width: 575px;}
    }

    .brew-sessions-wrapper .brew-sessions  .graph {
      margin-left: 0;
      margin-right: 0;
    }

    .three-columns {
      display: flex;
      flex-wrap: wrap;

      & > div {
        width: 30%;
        min-width: 300px;
        max-width: 500px;
        margin: 0;
      }
    }
  }

</style>
